import Head from "next/head";
import Link from "next/link";
import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Purchase Arizona Diamondbacks Upgrades",
    description:
      "Enhance your game-day experience by upgrading your Arizona Diamondbacks tickets here. Upgrades are non-refundable. Please use your dbacks.com email to purchase your tickets.",
    authRedirectPage: routes.home,
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How come I don't see seat upgrades available for purchase?",
    answer: "Upgrades are only eligible for those with a game ticket already.",
  },
  {
    question: "Will there be seat upgrades available for every game?",
    answer:
      "Upgrades are subject to availability, please check back in on the day of the game to see eligible offers.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After successful purchase, seat upgrades will be delivered within your Ballpark app. Please note that these tickets are not eligible for entry into Chase Field.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the sections noted in your seat upgrade offer.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please head to Ticket Resolution for any assistance you may require.",
  },
];

export const TERMS_AND_CONDITION = () => {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from the Arizona Diamondbacks. You may unsubscribe at
      any time. By checking this box you agree to receive additional commercial
      emails from the Arizona Diamondbacks and MLB . The terms and conditions of
      your ticket and policies applicable to its use are updated to address
      circumstances related to COVID-19 and health and safety. By clicking the
      Purchase Tickets button, you agree to accept all{" "}
      <Link
        href={`${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`}
        target="_blank"
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target="_blank"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
        }
        target="_blank"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Diamondbacks account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only. MLB and supported mobile device required.
    </>
  );
};

export const REGISTRATION_TERMS_AND_CONDITION = () => {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from the Arizona Diamondbacks. You may unsubscribe at
      any time. By checking this box you agree to receive additional commercial
      emails from the Arizona Diamondbacks and MLB . The terms and conditions of
      your ticket and policies applicable to its use are updated to address
      circumstances related to COVID-19 and health and safety. By clicking the
      Purchase Tickets button, you agree to accept all{" "}
      <Link
        href={`${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`}
        target="_blank"
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target="_blank"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use?_gl=1*1okouzm*_gcl_au*MjEwNDgzNzEzOS4xNzcxNTQyMTIxLjg4MjcwNDgwNS4xNzc0MDQ3NDY4LjE3NzQwNDc0Njc.*_ga*OTMyNTY1NzY3LjE3Njk2MjU2NzY.*_ga_N8YFCZLYSZ*czE3NzQwNDc0MzMkbzQkZzEkdDE3NzQwNDg2MzAkajUwJGwwJGgxOTYwMTg2MzAw"
        }
        target="_blank"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile of Use
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Diamondbacks account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only. MLB and supported mobile device required.
    </>
  );
};

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "Arizona Diamondbacks Terms and Conditions",
      onClick: () => {
        window.open(
          `${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`,
          "_self",
        );
      },
    },
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self",
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self",
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();

  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            `${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`,
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        Arizona Diamondbacks Terms and Conditions
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-google-tag-manager`}
        dangerouslySetInnerHTML={{
          __html: `
              (function(w,d,s,l,i){
                w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
                var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
                j.async=true;
                // j.src= 'https://url.us.m.mimecastprotect.com/s/_puYC73WLLSZyv0DC8fMIoKC0I'+i+dl;
                j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
                f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-3CSM3P');
              console.log('======azdbacks google tag manager loaded=====');
          `,
        }}
      />

      <noscript id={`${TEAM_SCHEMA}-google-tag-manager-noscript`}>
        <iframe
          src="https://url.us.m.mimecastprotect.com/s/LXVqC82B66fYQlAoU1hYIy5fRN"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>

      <script
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '1532627770340759');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1532627770340759&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
